import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Reveal, MaskedLine } from "@/components/Reveal";

const BPM = 76;

// chord tables: Dm / Gm / Dm / A — dub progression
const BARS = [
  { chord: [146.83, 174.61, 220.0], root: 73.42 },
  { chord: [196.0, 233.08, 293.66], root: 98.0 },
  { chord: [146.83, 174.61, 220.0], root: 73.42 },
  { chord: [220.0, 277.18, 329.63], root: 55.0 },
];

function buildEngine() {
  const ctx = new (window.AudioContext || window.webkitAudioContext)();
  const master = ctx.createGain();
  master.gain.value = 0;
  master.connect(ctx.destination);

  const noiseLen = ctx.sampleRate * 2;
  const noiseBuf = ctx.createBuffer(1, noiseLen, ctx.sampleRate);
  const nd = noiseBuf.getChannelData(0);
  for (let i = 0; i < noiseLen; i++) nd[i] = Math.random() * 2 - 1;

  // vinyl crackle bed
  const crackle = () => {
    if (ctx.state === "closed") return;
    const t = ctx.currentTime;
    const src = ctx.createBufferSource();
    src.buffer = noiseBuf;
    const hp = ctx.createBiquadFilter();
    hp.type = "highpass";
    hp.frequency.value = 3200;
    const g = ctx.createGain();
    g.gain.setValueAtTime(Math.random() * 0.05, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.02);
    src.connect(hp).connect(g).connect(master);
    src.start(t, Math.random() * 1.5, 0.03);
    setTimeout(crackle, 90 + Math.random() * 700);
  };
  crackle();

  const kick = (t) => {
    const o = ctx.createOscillator();
    o.frequency.setValueAtTime(100, t);
    o.frequency.exponentialRampToValueAtTime(38, t + 0.14);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.85, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.32);
    o.connect(g).connect(master);
    o.start(t);
    o.stop(t + 0.34);
  };

  const rim = (t, v = 0.28) => {
    const src = ctx.createBufferSource();
    src.buffer = noiseBuf;
    const bp = ctx.createBiquadFilter();
    bp.type = "bandpass";
    bp.frequency.value = 3400;
    bp.Q.value = 2;
    const g = ctx.createGain();
    g.gain.setValueAtTime(v, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.07);
    src.connect(bp).connect(g).connect(master);
    src.start(t, Math.random(), 0.08);
    const o = ctx.createOscillator();
    o.type = "square";
    o.frequency.value = 820;
    const og = ctx.createGain();
    og.gain.setValueAtTime(v * 0.5, t);
    og.gain.exponentialRampToValueAtTime(0.001, t + 0.05);
    o.connect(og).connect(master);
    o.start(t);
    o.stop(t + 0.06);
  };

  const hat = (t, open) => {
    const src = ctx.createBufferSource();
    src.buffer = noiseBuf;
    const hp = ctx.createBiquadFilter();
    hp.type = "highpass";
    hp.frequency.value = 9000;
    const g = ctx.createGain();
    g.gain.setValueAtTime(open ? 0.1 : 0.055, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + (open ? 0.14 : 0.035));
    src.connect(hp).connect(g).connect(master);
    src.start(t, Math.random(), open ? 0.16 : 0.04);
  };

  // offbeat skank chop — two detuned saws through a bandpass, short and dry
  const skank = (t, chord) => {
    const bp = ctx.createBiquadFilter();
    bp.type = "bandpass";
    bp.frequency.value = 1150;
    bp.Q.value = 0.9;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.15, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.17);
    bp.connect(g).connect(master);
    chord.forEach((f) => {
      [0.997, 1.004].forEach((d) => {
        const o = ctx.createOscillator();
        o.type = "sawtooth";
        o.frequency.value = f * d;
        o.connect(bp);
        o.start(t);
        o.stop(t + 0.2);
      });
    });
  };

  // deep round dub bass
  const bass = (t, f, dur = 0.5) => {
    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = 260;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.001, t);
    g.gain.exponentialRampToValueAtTime(0.5, t + 0.03);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    lp.connect(g).connect(master);
    const o = ctx.createOscillator();
    o.type = "sine";
    o.frequency.value = f;
    o.connect(lp);
    o.start(t);
    o.stop(t + dur + 0.05);
    const o2 = ctx.createOscillator();
    o2.type = "triangle";
    o2.frequency.value = f * 2;
    const g2 = ctx.createGain();
    g2.gain.value = 0.25;
    o2.connect(g2).connect(lp);
    o2.start(t);
    o2.stop(t + dur + 0.05);
  };

  let step = 0;
  let bar = 0;
  let nextTime = ctx.currentTime + 0.1;
  const stepDur = 60 / BPM / 4;
  const timer = setInterval(() => {
    if (ctx.state === "closed") return clearInterval(timer);
    while (nextTime < ctx.currentTime + 0.25) {
      const s = step;
      const t = nextTime + (s % 2 ? 0.014 : 0); // lazy dub swing
      const { chord, root } = BARS[bar];
      const fifth = root * 1.5;
      const octave = root * 2;

      if (s % 2 === 0) hat(t, s === 14);
      if (s === 8) {
        kick(t);
        rim(t);
      }
      if (s === 10 && bar % 2 === 1) rim(t + stepDur * 0.5, 0.14);
      if ([2, 6, 10, 14].includes(s)) skank(t, chord);

      if (s === 0) bass(t, root, 0.55);
      if (s === 3) bass(t, root, 0.4);
      if (s === 6) bass(t, fifth, 0.45);
      if (s === 8) bass(t, root, 0.6);
      if (s === 11) bass(t, octave, 0.4);
      if (s === 14) bass(t, fifth, 0.5);

      step = (step + 1) % 16;
      if (step === 0) bar = (bar + 1) % BARS.length;
      nextTime += stepDur;
    }
  }, 90);

  return { ctx, master };
}

export default function Vinyl() {
  const [playing, setPlaying] = useState(false);
  const engineRef = useRef(null);

  useEffect(
    () => () => {
      engineRef.current?.ctx.close();
      engineRef.current = null;
    },
    []
  );

  const toggle = () => {
    if (!engineRef.current) engineRef.current = buildEngine();
    const { ctx, master } = engineRef.current;
    ctx.resume();
    const now = ctx.currentTime;
    master.gain.cancelScheduledValues(now);
    master.gain.setValueAtTime(master.gain.value, now);
    master.gain.linearRampToValueAtTime(playing ? 0 : 0.5, now + 0.9);
    setPlaying(!playing);
  };

  return (
    <section id="radio" data-testid="vinyl-section" className="bg-ink text-paper py-24 sm:py-32 overflow-hidden">
      <div className="mx-auto max-w-7xl px-5 sm:px-10 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <Reveal>
            <p className="font-sans text-turq text-xs font-semibold tracking-[0.35em] mb-6">INTERLUDE — LISTEN</p>
          </Reveal>
          <h2 className="font-display leading-[0.85]">
            <MaskedLine inView className="text-[clamp(2.8rem,9vw,7.5rem)]">
              SHUTUP <span className="text-pink">RADIO.</span>
            </MaskedLine>
          </h2>
          <Reveal delay={0.15} className="mt-8 max-w-md">
            <p className="font-sans text-paper/70 text-base leading-relaxed">
              A voice needs a frequency. Drop the needle on Side A — a reggae dub pressed live in your browser, for
              anyone who was ever told to keep it down.
            </p>
            <div className="mt-8 border border-white/15 divide-y divide-white/15 font-sans text-sm">
              <div className="flex justify-between gap-4 px-4 py-3.5">
                <span className="text-paper/50 tracking-[0.2em] text-xs shrink-0">SIDE A</span>
                <span className="font-bold tracking-wide text-right">SPEAK WILD DUB</span>
              </div>
              <div className="flex justify-between gap-4 px-4 py-3.5">
                <span className="text-paper/50 tracking-[0.2em] text-xs shrink-0">FORMAT</span>
                <span className="font-bold tracking-wide text-right">GENERATIVE REGGAE · 76 BPM · ∞ LOOP</span>
              </div>
              <div className="flex justify-between gap-4 px-4 py-3.5">
                <span className="text-paper/50 tracking-[0.2em] text-xs shrink-0">STATUS</span>
                <span className={`font-bold tracking-wide text-right ${playing ? "text-acid" : ""}`} data-testid="vinyl-status">
                  {playing ? "NOW PLAYING" : "NEEDLE UP"}
                </span>
              </div>
            </div>
            <p className="font-hand text-turq text-2xl mt-6 -rotate-2">
              want the real record? this one is pressed live, just for you.
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="relative mx-auto w-full max-w-md aspect-square" data-cursor="look">
            <div
              data-testid="vinyl-record"
              className={`vinyl-spin absolute inset-0 rounded-full border border-white/10 ${playing ? "" : "paused"}`}
              style={{
                background:
                  "repeating-radial-gradient(circle at 50% 50%, #0a0a0a 0px, #0a0a0a 2px, #161616 3px, #0a0a0a 4px)",
              }}
            >
              <div className="absolute inset-[31%] rounded-full bg-pink flex flex-col items-center justify-center border-4 border-black rotate-[-4deg]">
                <p className="font-display text-black text-lg sm:text-2xl leading-none tracking-wide">SHUTUP</p>
                <p className="font-display text-black/70 text-[10px] sm:text-xs mt-1 tracking-[0.2em]">RADIO</p>
                <p className="font-sans text-black/60 text-[8px] sm:text-[9px] font-bold tracking-[0.25em] mt-2">
                  SIDE A · 33⅓ RPM
                </p>
              </div>
              <div className="absolute left-1/2 top-1/2 w-2.5 h-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-paper" />
            </div>

            <motion.div
              className="absolute -top-4 -right-3 w-[46%] origin-[88%_8%]"
              animate={{ rotate: playing ? 26 : 0 }}
              transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
              data-testid="vinyl-tonearm"
            >
              <svg viewBox="0 0 200 200" fill="none" aria-hidden>
                <circle cx="176" cy="16" r="14" stroke="#F7F5F0" strokeWidth="3" strokeDasharray="5 4" />
                <path d="M176 16 L 60 150" stroke="#F7F5F0" strokeWidth="4" strokeLinecap="round" />
                <path d="M60 150 L 52 168" stroke="#FF10F0" strokeWidth="6" strokeLinecap="round" />
              </svg>
            </motion.div>

            <div className="absolute -bottom-14 left-1/2 -translate-x-1/2">
              <button
                data-testid="vinyl-toggle"
                onClick={toggle}
                aria-pressed={playing}
                className="group inline-flex items-center gap-3 border-2 border-paper/60 px-6 py-3 hover:border-acid transition-colors"
              >
                <span className={`w-2.5 h-2.5 rounded-full ${playing ? "bg-acid animate-pulse" : "bg-paper/50"}`} />
                <span className="font-hand text-2xl text-paper group-hover:text-acid transition-colors -rotate-1" data-testid="vinyl-toggle-label">
                  {playing ? "lift the needle." : "drop the needle →"}
                </span>
              </button>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
